// Leave Balances (admin) — at-a-glance view of "how many leaves are left
// per staff", computed from actual approved Leave docs + WFH attendance.
//
// Hierarchy:
//   1. Annual leave policy (small, collapsible) — sets the entitlement
//   2. Remaining Balances table (PRIMARY view, read-only, color-coded)
//   3. Manual override grid (collapsible, hidden by default) — only for
//      mid-cycle setup or rare data corrections. Setting a value here
//      establishes a floor: effective used = max(override, actual count).
//
// Backend returns both `actual` (count from records) and `override` (stored
// LeaveBalance doc) — the table shows the effective `used` and `remaining`,
// which automatically tracks every new approved leave without any admin
// touching the override grid.

import React, { useEffect, useState, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, Skeleton } from "../../Components/ui";

const inputCell = {
  width: 70,
  border: "1px solid var(--border-default)",
  borderRadius: 6,
  padding: "4px 6px",
  fontSize: 13,
  fontVariantNumeric: "tabular-nums",
  background: "var(--bg-surface)",
  color: "var(--text-primary)",
};
const noteInput = { ...inputCell, width: 140, fontVariantNumeric: "normal" };
const btnSave = {
  background: "var(--accent-primary)", color: "white", border: "none",
  borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
};
const btnGhost = {
  background: "transparent", color: "var(--text-secondary)",
  border: "1px solid var(--border-default)", borderRadius: 6,
  padding: "4px 10px", fontSize: 12, cursor: "pointer",
};

const th = {
  textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 600,
  textTransform: "uppercase", letterSpacing: "0.06em",
  color: "var(--text-tertiary)", background: "var(--bg-subtle)",
  borderBottom: "1px solid var(--border-subtle)",
};
const td = { padding: "10px 12px", color: "var(--text-primary)", borderTop: "1px solid var(--border-subtle)", verticalAlign: "top" };

function fullName(u) {
  return `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username || u.email || "—";
}

export default function LeaveBalancesPage() {
  const toast = useToast();
  const role = localStorage.getItem("role");
  const isAdmin = role === "SUPER_ADMIN";
  useShellHeader({ title: "Leave Balances", breadcrumb: "Leave & WFH · Balances" }, []);

  const [year, setYear] = useState(new Date().getUTCFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api(`/leaves/balances?year=${year}`);
      setData(d);
    } catch (e) {
      toast({ title: e.message, status: "error" });
    } finally {
      setLoading(false);
    }
  }, [year, toast]);

  useEffect(() => { load(); }, [load]);

  // Default the focused employee to the first row once data arrives, and keep
  // the selection valid if the roster changes (e.g. switching year).
  useEffect(() => {
    const rs = data?.rows || [];
    if (rs.length && !rs.some((r) => r.user._id === selectedId)) {
      setSelectedId(rs[0].user._id);
    }
  }, [data, selectedId]);

  if (!isAdmin) {
    return (
      <Card padding="lg">
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>Restricted</div>
        <div style={{ fontSize: 13, color: "var(--text-tertiary)", marginTop: 4 }}>
          Leave Balances is available to administrators only.
        </div>
      </Card>
    );
  }

  if (loading || !data) return <Skeleton.Card />;

  const policy = data.policy || { annual: 7, sick: 10, casual: 8, wfhPerMonth: 3 };
  const rows = data.rows || [];
  const wfhCap = policy.wfhPerMonth || policy.wfh || 3;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header + year picker */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>
            Leave Balances · {year}
          </h2>
          <div style={{ fontSize: 13, color: "var(--text-tertiary)", marginTop: 4 }}>
            How many leaves each employee has remaining this year. Numbers update automatically as leaves get approved.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 12, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>Year:</label>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} style={{ ...inputCell, width: 100 }}>
            {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button style={btnGhost} onClick={load}>Refresh</button>
        </div>
      </div>

      {/* Policy editor — collapsible */}
      <PolicyEditor onSaved={load} />

      {/* Per-employee focus — PRIMARY VIEW */}
      <EmployeeFocus
        rows={rows}
        policy={policy}
        wfhCap={wfhCap}
        selectedId={selectedId}
        setSelectedId={setSelectedId}
        search={search}
        setSearch={setSearch}
      />

      {/* All employees — secondary, collapsible (nothing is lost) */}
      <Card padding="none">
        <div
          onClick={() => setShowAll((v) => !v)}
          style={{ padding: "12px 16px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>All employees</div>
            <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginTop: 2 }}>
              The full {rows.length}-person balance table, for scanning everyone at once.
            </div>
          </div>
          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{showAll ? "Hide" : "Show"}</span>
        </div>
        {showAll && (
          <div style={{ overflowX: "auto", borderTop: "1px solid var(--border-subtle)" }}>
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Employee</th>
                  <th style={th}>Annual</th>
                  <th style={th}>Sick</th>
                  <th style={th}>Casual</th>
                  <th style={th}>WFH this month</th>
                  <th style={th}>Total leaves taken</th>
                  <th style={th}>Carry over</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td style={{ ...td, textAlign: "center", color: "var(--text-tertiary)" }} colSpan={7}>No active staff yet.</td></tr>
                ) : rows.map((r) => (
                  <BalanceRow key={r._id || r.user._id} r={r} policy={policy} wfhCap={wfhCap} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Manual override — collapsed by default. Most admins never need this. */}
      <Card padding="none">
        <div
          onClick={() => setOverrideOpen((v) => !v)}
          style={{ padding: "12px 16px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
              ⚙ Manual override — only for mid-cycle setup or corrections
            </div>
            <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginTop: 2 }}>
              Set a floor for "already used" counts. The table above will use whichever is larger — your override or the actual count from approved leaves.
            </div>
          </div>
          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{overrideOpen ? "Hide" : "Show"}</span>
        </div>
        {overrideOpen && <OverrideGrid rows={rows} policy={policy} wfhCap={wfhCap} onSaved={load} />}
      </Card>
    </div>
  );
}

// One row of the remaining-balances primary table — color-coded green/red.
function BalanceRow({ r, policy, wfhCap }) {
  const annTotal = policy.annual + (r.carryOver || 0);
  const remAnn  = r.remaining?.annual ?? 0;
  const remSick = r.remaining?.sick   ?? 0;
  const remCas  = r.remaining?.casual ?? 0;
  const wfhMonth = r.actual?.wfhThisMonth ?? 0;
  const wfhRem   = Math.max(0, wfhCap - wfhMonth);

  const cell = (used, total, rem) => {
    const color = rem <= 0 ? "var(--tone-danger-fg)" : (rem <= Math.max(1, total * 0.25) ? "var(--tone-warning-fg)" : "var(--tone-success-fg)");
    return (
      <td style={td}>
        <span style={{ color, fontWeight: 700 }}>{rem} left</span>
        <span style={{ color: "var(--text-tertiary)", fontSize: 11, marginLeft: 4 }}>· {used}/{total} used</span>
      </td>
    );
  };
  const wfhCell = () => {
    const color = wfhRem <= 0 ? "var(--tone-danger-fg)" : "var(--tone-success-fg)";
    return (
      <td style={td}>
        <span style={{ color, fontWeight: 700 }}>{wfhRem} left</span>
        <span style={{ color: "var(--text-tertiary)", fontSize: 11, marginLeft: 4 }}>· {wfhMonth}/{wfhCap} used</span>
      </td>
    );
  };

  return (
    <tr>
      <td style={td}>
        <div style={{ fontWeight: 600 }}>{fullName(r.user)}</div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{r.user.team?.name || r.user.email}</div>
      </td>
      {cell(r.used.annual, annTotal, remAnn)}
      {cell(r.used.sick,   policy.sick,   remSick)}
      {cell(r.used.casual, policy.casual, remCas)}
      {wfhCell()}
      <td className="tabular" style={td}>
        <span style={{ fontWeight: 600 }}>{r.actual?.totalLeaveDays ?? 0}</span>
        <span style={{ color: "var(--text-tertiary)", fontSize: 11, marginLeft: 4 }}>this year</span>
      </td>
      <td className="tabular" style={td}>{r.carryOver || 0} d</td>
    </tr>
  );
}

// Per-employee focus — the PRIMARY view. A searchable selector picks one
// employee; their balances render as a few sized, labelled cards.
function EmployeeFocus({ rows, policy, wfhCap, selectedId, setSelectedId, search, setSearch }) {
  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter((r) => fullName(r.user).toLowerCase().includes(q) || (r.user.email || "").toLowerCase().includes(q))
    : rows;
  const selected = rows.find((r) => r.user._id === selectedId) || null;
  // Always keep the currently-selected employee in the dropdown, even if the
  // search text would otherwise filter them out, so the selection stays valid.
  const options = selected && !filtered.some((r) => r.user._id === selected.user._id)
    ? [selected, ...filtered]
    : filtered;

  return (
    <Card padding="md">
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: selected ? 16 : 0 }}>
        <input
          style={{ ...inputCell, width: 220, maxWidth: "100%", fontVariantNumeric: "normal" }}
          placeholder="Search employee…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          style={{ ...inputCell, width: 280, maxWidth: "100%", fontVariantNumeric: "normal" }}
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
        >
          {options.length === 0 && <option value="">No matching employee</option>}
          {options.map((r) => (
            <option key={r.user._id} value={r.user._id}>
              {fullName(r.user)}{r.user.email ? ` · ${r.user.email}` : ""}
            </option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
          {filtered.length} of {rows.length}
        </span>
      </div>

      {selected ? (
        <SelectedBalances r={selected} policy={policy} wfhCap={wfhCap} />
      ) : (
        <div style={{ fontSize: 13, color: "var(--text-tertiary)" }}>
          {rows.length === 0 ? "No active staff yet." : "Select an employee to see their balances."}
        </div>
      )}
    </Card>
  );
}

// The selected employee's header + card grid.
function SelectedBalances({ r, policy, wfhCap }) {
  const annTotal = policy.annual + (r.carryOver || 0);
  const cards = [
    { key: "annual", label: "Annual", used: r.used?.annual ?? 0, total: annTotal, rem: r.remaining?.annual ?? 0,
      sub: r.carryOver ? `includes ${r.carryOver}d carried over` : null },
    { key: "sick",   label: "Sick",   used: r.used?.sick   ?? 0, total: policy.sick,   rem: r.remaining?.sick   ?? 0 },
    { key: "casual", label: "Casual", used: r.used?.casual ?? 0, total: policy.casual, rem: r.remaining?.casual ?? 0 },
    { key: "wfh",    label: "WFH · this month", used: r.actual?.wfhThisMonth ?? 0, total: wfhCap,
      rem: Math.max(0, wfhCap - (r.actual?.wfhThisMonth ?? 0)), perMonth: true },
  ];

  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-primary)" }}>{fullName(r.user)}</div>
          <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>{r.user.team?.name || r.user.email || ""}</div>
        </div>
        <div className="tabular" style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
          {r.actual?.totalLeaveDays ?? 0} total leave days taken this year
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
        {cards.map((c) => <BalanceCard key={c.key} {...c} />)}
      </div>
    </>
  );
}

// A single leave-type card: entitled / taken / remaining, legible at a glance.
function BalanceCard({ label, used, total, rem, sub, perMonth }) {
  const hasEntitlement = total > 0;
  const color = !hasEntitlement ? "var(--text-tertiary)"
    : rem <= 0 ? "var(--tone-danger-fg)"
    : rem <= Math.max(1, total * 0.25) ? "var(--tone-warning-fg)"
    : "var(--tone-success-fg)";
  const pct = hasEntitlement ? Math.min(100, Math.round((used / total) * 100)) : 0;

  return (
    <Card variant="subtle" padding="md">
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-tertiary)" }}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 6 }}>
        <span className="tabular" style={{ fontSize: 30, fontWeight: 700, lineHeight: 1, color }}>
          {hasEntitlement ? rem : "—"}
        </span>
        <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{hasEntitlement ? "left" : "no entitlement"}</span>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: "var(--border-subtle)", marginTop: 10, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 999 }} />
      </div>
      <div className="tabular" style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 8 }}>
        {used} of {total} used{perMonth ? " this month" : ""}
      </div>
      {sub && <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>{sub}</div>}
    </Card>
  );
}

// Manual override grid — same editable inputs that used to be the page's
// PRIMARY view in the WP plugin, now hidden behind a disclosure.
function OverrideGrid({ rows, policy, wfhCap, onSaved }) {
  const toast = useToast();
  const [edits, setEdits] = useState(() => {
    const buf = {};
    for (const r of rows) {
      if (!r._id) continue;
      buf[r._id] = {
        annualUsed: r.override?.annual ?? r.used?.annual ?? 0,
        sickUsed:   r.override?.sick   ?? r.used?.sick   ?? 0,
        casualUsed: r.override?.casual ?? r.used?.casual ?? 0,
        wfhUsed:    r.override?.wfh    ?? r.used?.wfh    ?? 0,
        carryOver:  r.carryOver ?? 0,
        notes:      r.notes ?? "",
      };
    }
    return buf;
  });
  const [rowState, setRowState] = useState({});

  function setField(id, field, value) {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
    setRowState((prev) => ({ ...prev, [id]: "dirty" }));
  }
  async function save(id) {
    const p = edits[id];
    if (!p) return;
    setRowState((prev) => ({ ...prev, [id]: "saving" }));
    try {
      await api(`/leaves/balance/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          annualUsed: Number(p.annualUsed) || 0,
          sickUsed:   Number(p.sickUsed)   || 0,
          casualUsed: Number(p.casualUsed) || 0,
          wfhUsed:    Number(p.wfhUsed)    || 0,
          carryOver:  Number(p.carryOver)  || 0,
          notes:      p.notes || "",
        }),
      });
      setRowState((prev) => ({ ...prev, [id]: "saved" }));
      setTimeout(() => setRowState((prev) => ({ ...prev, [id]: undefined })), 2000);
      onSaved && onSaved();
    } catch (e) {
      setRowState((prev) => ({ ...prev, [id]: "error" }));
      toast({ title: e.data?.message || e.message, status: "error" });
    }
  }

  return (
    <div style={{ borderTop: "1px solid var(--border-subtle)" }}>
      <div style={{
        background: "var(--tone-warning-bg)", borderBottom: "1px solid var(--tone-warning-br)",
        padding: "10px 14px", fontSize: 12, color: "var(--tone-warning-fg)",
      }}>
        Use this only for <strong>mid-cycle onboarding</strong> (an employee took leave before this system existed)
        or to correct a wrong count. The table above will use the higher of your override or the actual count.
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Staff</th>
              <th style={th}>Annual</th>
              <th style={th}>Sick</th>
              <th style={th}>Casual</th>
              <th style={th}>WFH</th>
              <th style={th}>Carry</th>
              <th style={th}>Notes</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const id = r._id;
              const e = edits[id] || {};
              const s = rowState[id];
              return (
                <tr key={id || r.user._id}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{fullName(r.user)}</div>
                    <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                      actual: {r.actual?.annual || 0}/{r.actual?.sick || 0}/{r.actual?.casual || 0}/{r.actual?.wfh || 0}
                    </div>
                  </td>
                  <td style={td}><input type="number" min="0" max={policy.annual + 30} style={inputCell} value={e.annualUsed ?? 0} onChange={(ev) => setField(id, "annualUsed", ev.target.value)} /></td>
                  <td style={td}><input type="number" min="0" max={policy.sick   + 30} style={inputCell} value={e.sickUsed   ?? 0} onChange={(ev) => setField(id, "sickUsed",   ev.target.value)} /></td>
                  <td style={td}><input type="number" min="0" max={policy.casual + 30} style={inputCell} value={e.casualUsed ?? 0} onChange={(ev) => setField(id, "casualUsed", ev.target.value)} /></td>
                  <td style={td}><input type="number" min="0" style={inputCell} value={e.wfhUsed    ?? 0} onChange={(ev) => setField(id, "wfhUsed",    ev.target.value)} /></td>
                  <td style={td}><input type="number" min="0" max="30" style={inputCell} value={e.carryOver ?? 0} onChange={(ev) => setField(id, "carryOver", ev.target.value)} /></td>
                  <td style={td}><input type="text" style={noteInput} value={e.notes ?? ""} onChange={(ev) => setField(id, "notes", ev.target.value)} placeholder="e.g. pre-onboarding" /></td>
                  <td style={td}>
                    <button style={btnSave} disabled={s === "saving"} onClick={() => save(id)}>
                      {s === "saving" ? "Saving…" : "Save"}
                    </button>
                    {s === "saved" && <div style={{ fontSize: 11, color: "var(--tone-success-fg)", marginTop: 4 }}>✓</div>}
                    {s === "error" && <div style={{ fontSize: 11, color: "var(--tone-danger-fg)", marginTop: 4 }}>err</div>}
                    {s === "dirty" && <div style={{ fontSize: 11, color: "var(--tone-warning-fg)", marginTop: 4 }}>•</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Policy editor — collapsible. PUT /api/leaves/policy.
function PolicyEditor({ onSaved }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api("/leaves/policy").then((d) => setPolicy(d.policy)).catch(() => {});
  }, []);

  async function save() {
    if (!policy) return;
    setSaving(true);
    try {
      const d = await api("/leaves/policy", { method: "PUT", body: JSON.stringify(policy) });
      setPolicy(d.policy);
      toast({ title: "Policy saved", status: "success" });
      onSaved && onSaved();
    } catch (e) {
      toast({ title: e.data?.message || e.message || "Save failed", status: "error" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card padding="none">
      <div
        onClick={() => setOpen((v) => !v)}
        style={{ padding: "12px 16px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Annual leave policy</div>
          <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginTop: 2 }}>
            {policy ? `${policy.annual} annual · ${policy.sick} sick · ${policy.casual} casual days/year` : "Loading…"}
          </div>
        </div>
        <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{open ? "Hide" : "Edit"}</span>
      </div>
      {open && policy && (
        <div style={{ padding: 16, borderTop: "1px solid var(--border-subtle)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
            {["annual", "sick", "casual"].map((k) => (
              <div key={k}>
                <div style={{ fontSize: 10, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, marginBottom: 4 }}>{k}</div>
                <input type="number" min={0} max={365} style={{ ...inputCell, width: "100%" }}
                  value={policy[k] ?? 0}
                  onChange={(e) => setPolicy({ ...policy, [k]: parseFloat(e.target.value) || 0 })} />
              </div>
            ))}
          </div>
          <div style={{ marginTop: 14 }}>
            <button style={btnSave} disabled={saving} onClick={save}>{saving ? "Saving…" : "Save policy"}</button>
          </div>
        </div>
      )}
    </Card>
  );
}
